import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, User as UserIcon } from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { CustomButton } from '@/components/molecules/CustomButton';
import { CustomInput } from '@/components/molecules/CustomInput';
import { useMeUser } from '@/hooks/useUser';
import { updateMe } from '@/services/user';
import { useToast } from '@/hooks/useToastContext';
import { userAvatarFallback } from '@/utils/userAvatar';

export const Profile = () => {
  const { meUser } = useMeUser();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [username, setUsername] = useState(meUser?.username ?? '');
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoError, setInfoError] = useState('');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdErrors, setPwdErrors] = useState<{ current?: string; new?: string }>({});

  const handleInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInfoError('');
    if (username === meUser?.username) {
      showToast('info', 'Aucune modification à enregistrer.');
      return;
    }
    const payload = { username };
    setInfoLoading(true);
    try {
      await updateMe(payload);
      await queryClient.invalidateQueries({ queryKey: ['meUser'] });
      showToast('success', 'Profil mis à jour !');
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      setInfoError(message || 'Une erreur est survenue.');
    } finally {
      setInfoLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const hasPassword = meUser?.password !== null;
    const errors: { current?: string; new?: string } = {};
    if (hasPassword && !currentPassword) errors.current = 'Requis.';
    if (!newPassword) errors.new = 'Requis.';
    else if (newPassword.length < 8) errors.new = 'Minimum 8 caractères.';
    if (Object.keys(errors).length) {
      setPwdErrors(errors);
      return;
    }
    setPwdErrors({});
    setPwdLoading(true);
    try {
      const payload = hasPassword
        ? { currentPassword, newPassword }
        : { newPassword };
      await updateMe(payload);
      await queryClient.invalidateQueries({ queryKey: ['meUser'] });
      setCurrentPassword('');
      setNewPassword('');
      showToast('success', 'Mot de passe mis à jour !');
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message === 'Mot de passe actuel incorrect') {
        setPwdErrors({ current: 'Mot de passe actuel incorrect.' });
      } else if (message === 'currentPassword est requis pour modifier un mot de passe existant') {
        setPwdErrors({ current: 'Requis.' });
      } else if (message === 'newPassword est requis') {
        setPwdErrors({ new: 'Requis.' });
      } else {
        showToast('error', message || 'Une erreur est survenue.');
      }
    } finally {
      setPwdLoading(false);
    }
  };

  const [hasAvatarError, setHasAvatarError] = useState(false);

  return (
    <main className="min-h-screen">
      <div className="mx-auto flex w-full max-w-lg flex-col gap-6 p-5">
        <div className="flex flex-col items-center gap-3 pt-2">
          <div className="bg-primary-500/20 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full shadow-md">
            {meUser?.avatarUrl && !hasAvatarError ? (
              <img
                src={meUser.avatarUrl}
                alt={meUser.username}
                className="h-full w-full object-cover"
                onError={() => setHasAvatarError(true)}
              />
            ) : (
              <span className="text-content font-lexend text-2xl font-bold uppercase">
                {meUser ? userAvatarFallback(meUser.username) : '?'}
              </span>
            )}
          </div>
          <p className="text-content font-lexend text-xl font-semibold">
            {meUser?.username ?? '…'}
          </p>
        </div>

        <GenericCard
          title="Informations générales"
          subtitle="Pseudo et avatar"
        >
          <form onSubmit={handleInfoSubmit} className="mt-2 flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-content/60 text-xs font-semibold uppercase tracking-wide">
                Pseudo
              </label>
              <CustomInput
                value={username}
                onChange={setUsername}
                placeholder="Ton pseudo"
                fullWidth
                autoComplete="username"
              />
            </div>
            {infoError && (
              <p className="text-red-500 text-sm">{infoError}</p>
            )}
            <CustomButton
              type="submit"
              title="Enregistrer"
              color="primary"
              rounded="full"
              icon={<UserIcon size={15} />}
              loading={infoLoading}
            />
          </form>
        </GenericCard>

        <GenericCard
          title={meUser?.password === null ? 'Définir un mot de passe' : 'Changer le mot de passe'}
          subtitle={meUser?.password === null ? 'Ce compte n\'a pas encore de mot de passe' : 'Laisser vide si aucun changement'}
        >
          <form onSubmit={handlePasswordSubmit} className="mt-2 flex flex-col gap-4">
            {meUser?.password !== null && (
              <div className="flex flex-col gap-1">
                <label className="text-content/60 text-xs font-semibold uppercase tracking-wide">
                  Mot de passe actuel
                </label>
                <CustomInput
                  value={currentPassword}
                  onChange={(v) => {
                    setCurrentPassword(v);
                    setPwdErrors((prev) => ({ ...prev, current: undefined }));
                  }}
                  type="password"
                  placeholder="••••••••"
                  fullWidth
                  autoComplete="current-password"
                />
                {pwdErrors.current && (
                  <p className="text-red-500 text-xs">{pwdErrors.current}</p>
                )}
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-content/60 text-xs font-semibold uppercase tracking-wide">
                Nouveau mot de passe
              </label>
              <CustomInput
                value={newPassword}
                onChange={(v) => {
                  setNewPassword(v);
                  setPwdErrors((prev) => ({ ...prev, new: undefined }));
                }}
                type="password"
                placeholder="••••••••"
                fullWidth
                autoComplete="new-password"
              />
              {pwdErrors.new && (
                <p className="text-red-500 text-xs">{pwdErrors.new}</p>
              )}
            </div>
            <CustomButton
              type="submit"
              title={meUser?.password === null ? 'Définir le mot de passe' : 'Changer le mot de passe'}
              color="secondary"
              rounded="full"
              icon={<KeyRound size={15} />}
              loading={pwdLoading}
            />
          </form>
        </GenericCard>
      </div>
    </main>
  );
};